/**
 * Centralized Analytics Tracking Utility
 * Handles Google Analytics, Google Tag Manager, and Hotjar events
 */

// Check if analytics is available
const isAnalyticsAvailable = () => {
  return typeof window !== 'undefined' && window.gtag;
};

const isHotjarAvailable = () => {
  return typeof window !== 'undefined' && window.hj;
};

// Core tracking function
const trackEvent = (eventName, parameters = {}) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', eventName, {
      event_category: parameters.category || 'User Interaction',
      event_label: parameters.label || '',
      value: parameters.value || 0,
      custom_parameter: parameters.custom || '',
      page_path: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...parameters
    });
  }
  
  // Console log for debugging (remove in production)
  console.log('Analytics Event:', eventName, parameters);
};

// Conversion tracking
const trackConversion = (conversionId, value = 0, currency = 'USD') => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'conversion', {
      send_to: conversionId,
      value: value,
      currency: currency
    });
  }
};

// Page view tracking
const trackPageView = (pagePath, pageTitle) => {
  if (isAnalyticsAvailable()) {
    window.gtag('config', 'G-WJYNQH51NY', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

// Hotjar events
const trackHotjarEvent = (eventName) => {
  if (isHotjarAvailable()) {
    window.hj('event', eventName);
  }
};

// =============================================================================
// MARKETING SITE ANALYTICS (Already implemented but centralized here)
// =============================================================================

export const MarketingAnalytics = {
  // Homepage interactions
  heroCtaClick: () => trackEvent('hero_cta_click', { category: 'Marketing', label: 'Hero CTA' }),
  productGridClick: (productName) => trackEvent('product_grid_click', { category: 'Marketing', label: productName }),
  consultationBooking: () => {
    trackEvent('consultation_booking', { category: 'Marketing', label: 'Consultation Request' });
    trackConversion('AW-16872295585/O8D7CKns6Z4aEKGZq-0-');
  },
  
  // Product pages
  productPageView: (productName) => trackEvent('product_page_view', { category: 'Marketing', label: productName }),
  productCtaClick: (productName) => trackEvent('product_cta_click', { category: 'Marketing', label: productName }),
  
  // Resource engagement
  resourceDownload: (resourceName) => trackEvent('resource_download', { category: 'Marketing', label: resourceName }),
  articleView: (articleSlug) => trackEvent('article_view', { category: 'Content', label: articleSlug }),
  
  // Contact and social
  contactFormSubmit: () => trackEvent('contact_form_submit', { category: 'Marketing', label: 'Contact Form' }),
  socialClick: (platform) => trackEvent('social_click', { category: 'Marketing', label: platform }),
  
  // Navigation
  navigationClick: (section) => trackEvent('navigation_click', { category: 'Marketing', label: section })
};

// =============================================================================
// SERVICE MANAGEMENT ANALYTICS (New comprehensive tracking)
// =============================================================================

export const ServiceAnalytics = {
  
  // ===== AUTHENTICATION & ONBOARDING =====
  
  // 1-5: Registration & Login Flow
  registrationStart: () => {
    trackEvent('registration_start', { category: 'Authentication', label: 'Registration Started' });
    trackHotjarEvent('registration_start');
  },
  
  registrationComplete: (method = 'email') => {
    trackEvent('registration_complete', { category: 'Authentication', label: `Registration Complete - ${method}` });
    trackConversion('AW-16872295585/signup_conversion');
    trackHotjarEvent('registration_complete');
  },
  
  loginAttempt: (method = 'email') => trackEvent('login_attempt', { category: 'Authentication', label: `Login Attempt - ${method}` }),
  
  loginSuccess: (method = 'email') => {
    trackEvent('login_success', { category: 'Authentication', label: `Login Success - ${method}` });
    trackHotjarEvent('login_success');
  },
  
  loginFailure: (reason = 'unknown') => trackEvent('login_failure', { category: 'Authentication', label: `Login Failed - ${reason}` }),
  
  // ===== PLG FUNNEL TRACKING =====
  
  // 6-10: Core PLG Entry Points
  demoPageView: () => {
    trackEvent('demo_page_view', { category: 'PLG Funnel', label: 'Demo Page Viewed' });
    trackHotjarEvent('demo_page_view');
  },
  
  videoUploadStart: () => {
    trackEvent('video_upload_start', { category: 'PLG Funnel', label: 'Video Upload Started' });
    trackHotjarEvent('video_upload_start');
  },
  
  videoUploadComplete: (fileSize, duration) => {
    trackEvent('video_upload_complete', { 
      category: 'PLG Funnel', 
      label: 'Video Upload Complete',
      value: Math.round(fileSize / 1024 / 1024), // MB
      custom: `duration_${duration}s`
    });
    trackConversion('AW-16872295585/video_upload_conversion');
    trackHotjarEvent('video_upload_complete');
  },
  
  videoUploadError: (errorType) => trackEvent('video_upload_error', { category: 'PLG Funnel', label: `Upload Error - ${errorType}` }),
  
  demoProcessingComplete: (analysisType) => {
    trackEvent('demo_processing_complete', { category: 'PLG Funnel', label: `Demo Complete - ${analysisType}` });
    trackHotjarEvent('demo_processing_complete');
  },
  
  // 11-15: Business Case & ROI Calculator
  businessCaseStart: () => {
    trackEvent('business_case_start', { category: 'PLG Funnel', label: 'Business Case Started' });
    trackHotjarEvent('business_case_start');
  },
  
  roiCalculation: (estimatedRoi, industry) => {
    trackEvent('roi_calculation', { 
      category: 'PLG Funnel', 
      label: `ROI Calculated - ${industry}`,
      value: Math.round(estimatedRoi)
    });
    trackHotjarEvent('roi_calculation');
  },
  
  siteAssessmentComplete: (siteCount, complexity) => {
    trackEvent('site_assessment_complete', { 
      category: 'PLG Funnel', 
      label: `Site Assessment - ${complexity}`,
      value: siteCount
    });
  },
  
  businessCaseDownload: () => {
    trackEvent('business_case_download', { category: 'PLG Funnel', label: 'Business Case Downloaded' });
    trackConversion('AW-16872295585/business_case_conversion');
  },
  
  shareBusinessCase: (method) => trackEvent('business_case_share', { category: 'PLG Funnel', label: `Shared via ${method}` }),
  
  // ===== IMPLEMENTATION & PROJECT MANAGEMENT =====
  
  // 16-20: Implementation Planning
  implementationStart: () => {
    trackEvent('implementation_start', { category: 'Implementation', label: 'Implementation Planning Started' });
    trackHotjarEvent('implementation_start');
  },
  
  projectCreated: (projectType, complexity) => {
    trackEvent('project_created', { 
      category: 'Implementation', 
      label: `Project Created - ${projectType}`,
      custom: complexity
    });
  },
  
  hardwareSpecDownload: (specType) => trackEvent('hardware_spec_download', { category: 'Implementation', label: `Hardware Spec - ${specType}` }),
  
  installationScheduled: (timeframe) => {
    trackEvent('installation_scheduled', { category: 'Implementation', label: `Installation - ${timeframe}` });
    trackConversion('AW-16872295585/installation_scheduled');
  },
  
  implementationComplete: (projectId, duration) => {
    trackEvent('implementation_complete', { 
      category: 'Implementation', 
      label: 'Implementation Completed',
      value: duration,
      custom: projectId
    });
  },
  
  // ===== DASHBOARD & ANALYTICS USAGE =====
  
  // 21-25: Dashboard Engagement
  dashboardView: (tab) => {
    trackEvent('dashboard_view', { category: 'Dashboard', label: `Dashboard - ${tab}` });
    trackHotjarEvent('dashboard_view');
  },
  
  analyticsExport: (exportType, dataRange) => trackEvent('analytics_export', { 
    category: 'Dashboard', 
    label: `Export - ${exportType}`,
    custom: dataRange
  }),
  
  reportGeneration: (reportType, timeframe) => trackEvent('report_generation', { 
    category: 'Dashboard', 
    label: `Report - ${reportType}`,
    custom: timeframe
  }),
  
  alertConfiguration: (alertType) => trackEvent('alert_configuration', { category: 'Dashboard', label: `Alert - ${alertType}` }),
  
  dataVisualizationInteraction: (chartType, action) => trackEvent('data_viz_interaction', { 
    category: 'Dashboard', 
    label: `${chartType} - ${action}`
  }),
  
  // ===== ACCOUNT MANAGEMENT =====
  
  // 26-30: Account & Billing
  accountSettingsView: () => trackEvent('account_settings_view', { category: 'Account', label: 'Settings Viewed' }),
  
  profileUpdate: (fieldUpdated) => trackEvent('profile_update', { category: 'Account', label: `Updated - ${fieldUpdated}` }),
  
  billingPageView: () => {
    trackEvent('billing_page_view', { category: 'Billing', label: 'Billing Page Viewed' });
    trackHotjarEvent('billing_page_view');
  },
  
  subscriptionUpgrade: (fromTier, toTier, value) => {
    trackEvent('subscription_upgrade', { 
      category: 'Billing', 
      label: `${fromTier} to ${toTier}`,
      value: value
    });
    trackConversion('AW-16872295585/subscription_upgrade', value);
    trackHotjarEvent('subscription_upgrade');
  },
  
  subscriptionDowngrade: (fromTier, toTier) => trackEvent('subscription_downgrade', { 
    category: 'Billing', 
    label: `${fromTier} to ${toTier}`
  }),
  
  // 31-35: Payment & Billing Events
  paymentMethodAdd: (paymentType) => trackEvent('payment_method_add', { category: 'Billing', label: `Added - ${paymentType}` }),
  
  paymentSuccess: (amount, tier) => {
    trackEvent('payment_success', { 
      category: 'Billing', 
      label: `Payment - ${tier}`,
      value: amount
    });
    trackConversion('AW-16872295585/payment_success', amount);
  },
  
  paymentFailure: (reason, amount) => trackEvent('payment_failure', { 
    category: 'Billing', 
    label: `Payment Failed - ${reason}`,
    value: amount
  }),
  
  invoiceDownload: (invoiceId) => trackEvent('invoice_download', { category: 'Billing', label: `Invoice - ${invoiceId}` }),
  
  subscriptionCancel: (reason, tier) => {
    trackEvent('subscription_cancel', { category: 'Billing', label: `Cancelled - ${tier} - ${reason}` });
    trackHotjarEvent('subscription_cancel');
  },
  
  // ===== ADVANCED FEATURES =====
  
  // 36-40: Enterprise Features
  apiKeyGenerated: (keyType) => trackEvent('api_key_generated', { category: 'Enterprise', label: `API Key - ${keyType}` }),
  
  webhookConfigured: (webhookType) => trackEvent('webhook_configured', { category: 'Enterprise', label: `Webhook - ${webhookType}` }),
  
  dataExportRequest: (exportSize, format) => trackEvent('data_export_request', { 
    category: 'Enterprise', 
    label: `Export - ${format}`,
    value: exportSize
  }),
  
  integrationConnected: (integrationType) => {
    trackEvent('integration_connected', { category: 'Enterprise', label: `Integration - ${integrationType}` });
    trackConversion('AW-16872295585/integration_connected');
  },
  
  customAnalyticsRequest: (requestType) => trackEvent('custom_analytics_request', { category: 'Enterprise', label: `Custom - ${requestType}` }),
  
  // ===== SUPPORT & ENGAGEMENT =====
  
  // 41-45: Support & Help
  supportTicketCreated: (ticketType, priority) => trackEvent('support_ticket_created', { 
    category: 'Support', 
    label: `${ticketType} - ${priority}`
  }),
  
  helpDocumentView: (documentName) => trackEvent('help_document_view', { category: 'Support', label: documentName }),
  
  chatbotInteraction: (action) => trackEvent('chatbot_interaction', { category: 'Support', label: action }),
  
  feedbackSubmitted: (rating, category) => trackEvent('feedback_submitted', { 
    category: 'Support', 
    label: category,
    value: rating
  }),
  
  featureRequest: (requestedFeature) => trackEvent('feature_request', { category: 'Support', label: requestedFeature }),
  
  // ===== USER ENGAGEMENT & RETENTION =====
  
  // 46-50: Engagement & Retention
  sessionDuration: (duration) => trackEvent('session_duration', { 
    category: 'Engagement', 
    label: 'Session Length',
    value: Math.round(duration / 60) // minutes
  }),
  
  featureDiscovery: (featureName) => trackEvent('feature_discovery', { category: 'Engagement', label: featureName }),
  
  tooltipInteraction: (tooltipLocation) => trackEvent('tooltip_interaction', { category: 'Engagement', label: tooltipLocation }),
  
  emailNotificationPreference: (preference, enabled) => trackEvent('notification_preference', { 
    category: 'Engagement', 
    label: `${preference} - ${enabled ? 'enabled' : 'disabled'}`
  }),
  
  userRetention: (daysSinceSignup, loginCount) => trackEvent('user_retention', { 
    category: 'Engagement', 
    label: `Day ${daysSinceSignup}`,
    value: loginCount
  })
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

// Track user journey progression
export const trackJourneyStage = (fromStage, toStage) => {
  trackEvent('journey_progression', {
    category: 'User Journey',
    label: `${fromStage} → ${toStage}`,
    custom: `progression_${Date.now()}`
  });
};

// Track errors for debugging
export const trackError = (errorType, errorMessage, component) => {
  trackEvent('application_error', {
    category: 'Error',
    label: errorType,
    custom: `${component}: ${errorMessage}`
  });
};

// Enhanced page tracking for SPAs
export const trackSPAPageView = (pagePath, pageTitle, previousPage = null) => {
  trackPageView(pagePath, pageTitle);
  
  if (previousPage) {
    trackEvent('page_navigation', {
      category: 'Navigation',
      label: `${previousPage} → ${pagePath}`,
      custom: pageTitle
    });
  }
};

export default {
  MarketingAnalytics,
  ServiceAnalytics,
  trackEvent,
  trackConversion,
  trackPageView,
  trackJourneyStage,
  trackError,
  trackSPAPageView,
  trackHotjarEvent
};
